/**
 * Servicio de notificaciones por email para cotizaciones
 * 
 * Este servicio integra las funcionalidades de SendGrid con el flujo de cotizaciones
 * para enviar notificaciones automáticas en diferentes eventos.
 */

import Parse from '../../config/parse';
import { 
  sendQuotationConfirmation, 
  sendStatusUpdateNotification,
  sendWelcomeEmail,
  sendTemplateEmail,
  sendEmail,
  TEMPLATE_IDS
} from './sendgridService';

/**
 * Obtiene la URL base de la aplicación para generar enlaces
 * @returns {string} URL base de la aplicación
 */
const getAppUrl = () => {
  // Primero intentamos obtener la URL desde las variables de entorno
  const envUrl = import.meta.env.VITE_APP_URL;
  
  if (envUrl) {
    console.log('Usando URL de la aplicación desde variables de entorno:', envUrl);
    return envUrl;
  }
  
  // Si no está disponible, determinamos si estamos en desarrollo o producción
  const isProduction = import.meta.env.PROD;
  const defaultUrl = isProduction 
    ? 'https://vvhealth.vercel.app' 
    : 'http://localhost:5173';
  
  console.log(`URL de la aplicación no configurada en variables de entorno. Usando valor por defecto para ${isProduction ? 'producción' : 'desarrollo'}: ${defaultUrl}`);
  return defaultUrl;
};

/**
 * Envía una notificación de confirmación cuando se crea una nueva cotización
 * @param {Object} quotation - Objeto de cotización de Parse
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const notifyNewQuotation = async (quotation) => {
  try {
    const userEmail = quotation.get('userEmail');
    if (!userEmail) {
      console.warn('No se puede enviar notificación: email de usuario no disponible');
      return null;
    }

    // Obtener datos para la plantilla
    const userName = quotation.get('userName') || 'Cliente';
    const productType = quotation.get('productType') || 'Producto';
    const quotationId = quotation.id;
    
    // Obtener el magic link para continuar con la creación de cuenta
    const magicLink = quotation.get('magicLink');
    const appUrl = getAppUrl();
    
    // Construir URLs absolutas para los enlaces
    const trackingUrl = `${appUrl}/quotation/track/${magicLink}`;
    const detailUrl = `${appUrl}/quotation/detail/${magicLink}`;
    const authUrl = `${appUrl}/quotation/auth/${magicLink}`; // URL para crear cuenta
    
    console.log('Generando URLs para nueva cotización:', {
      magicLink,
      appUrl,
      trackingUrl,
      detailUrl,
      authUrl
    });

    // Usar la plantilla de bienvenida y solicitud de estimado
    const result = await sendTemplateEmail({
      to: userEmail,
      templateId: TEMPLATE_IDS.WELCOME_QUOTATION,
      dynamicTemplateData: {
        nombre_cliente: userName,
        email_cliente: userEmail,
        tipo_producto: productType,
        id_cotizacion: quotationId,
        enlace_seguimiento: trackingUrl, // Enlace de seguimiento de estado
        enlace_propuesta: detailUrl,     // Enlace para ver detalles completos
        enlace_cuenta: authUrl,          // Enlace para crear cuenta
        enlace_magic: magicLink,         // Magic link sin la URL base (por si la plantilla necesita construir URLs)
        app_url: appUrl                  // URL base de la aplicación
      }
    });

    if (result) {
      console.log(`Email de nueva cotización enviado exitosamente a ${userEmail} usando plantilla ${TEMPLATE_IDS.WELCOME_QUOTATION}`);
    } else {
      console.warn(`No se pudo enviar el email de nueva cotización a ${userEmail}`);
      // Intentar con el método de respaldo si falla la plantilla
      return await sendQuotationConfirmation(quotation, userEmail);
    }
    
    return result;
  } catch (error) {
    console.error('Error al enviar notificación de nueva cotización:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return null;
  }
};

/**
 * Envía una notificación cuando cambia el estado de una cotización
 * @param {Object} quotation - Objeto de cotización de Parse
 * @param {string} oldStatus - Estado anterior de la cotización
 * @param {string} newStatus - Nuevo estado de la cotización
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const notifyStatusChange = async (quotation, oldStatus, newStatus) => {
  try {
    // Si los estados son iguales, no enviamos notificación
    if (oldStatus === newStatus) {
      return null;
    }

    // Determinar el email del destinatario (usuario o email de cotización)
    let userEmail;
    let userName = 'Cliente';
    const user = quotation.get('user');
    
    if (user) {
      // Si la cotización está asociada a un usuario, obtenemos su email
      try {
        await user.fetch();
        userEmail = user.get('email');
        userName = user.get('name') || user.get('username') || 'Cliente';
      } catch (error) {
        console.warn('No se pudo obtener el email del usuario:', error);
      }
    }
    
    // Si no hay usuario o no se pudo obtener su email, usamos el email de la cotización
    if (!userEmail) {
      userEmail = quotation.get('userEmail');
      userName = quotation.get('userName') || 'Cliente';
    }

    if (!userEmail) {
      console.warn('No se puede enviar notificación: email de usuario no disponible');
      return null;
    }

    // Si el nuevo estado es 'estimado_listo', usamos la plantilla específica
    if (newStatus === 'estimado_listo') {
      const quotationId = quotation.id;
      
      // Generar URLs para seguimiento y detalle (magic link)
      const magicLink = quotation.get('magicLink');
      const appUrl = getAppUrl();
      const trackUrl = `${appUrl}/quotation/track/${magicLink}`;
      const detailUrl = `${appUrl}/quotation/detail/${magicLink}`;
      const authUrl = `${appUrl}/quotation/auth/${magicLink}`; // URL para crear cuenta
      
      console.log('Generando URLs para cotización lista:', {
        quotationId,
        magicLink,
        appUrl,
        trackUrl,
        detailUrl,
        authUrl
      });
      
      // Usar la plantilla de estimado listo
      const result = await sendTemplateEmail({
        to: userEmail,
        templateId: TEMPLATE_IDS.QUOTATION_READY,
        dynamicTemplateData: {
          nombre_cliente: userName,
          id_cotizacion: quotationId,
          enlace_propuesta: detailUrl,     // Enlace para ver detalles completos
          enlace_seguimiento: trackUrl,    // Enlace para seguimiento de estado
          enlace_cuenta: authUrl,          // Enlace para crear cuenta
          enlace_magic: magicLink,         // Magic link sin la URL base
          app_url: appUrl                  // URL base de la aplicación
        }
      });
      
      if (result) {
        console.log(`Email de estimado listo enviado exitosamente a ${userEmail} usando plantilla ${TEMPLATE_IDS.QUOTATION_READY}`);
      } else {
        console.warn(`No se pudo enviar el email de estimado listo a ${userEmail} usando plantilla. Intentando método alternativo...`);
        // Si falla la plantilla, intentamos con el método tradicional
        return await sendStatusUpdateNotification(quotation, userEmail, oldStatus, newStatus);
      }
      
      return result;
    }
    
    // Si el nuevo estado es 'rechazada', enviamos una notificación específica
    if (newStatus === 'rechazada') {
      const quotationId = quotation.id;
      
      // Enviamos un email personalizado para cotizaciones rechazadas
      return await sendEmail({
        to: userEmail,
        subject: 'Actualización sobre su cotización en VV Health',
        text: `Estimado/a ${userName},\n\nLamentamos informarle que no podemos proceder con su cotización #${quotationId} en este momento. Esto puede deberse a que los productos solicitados no están disponibles o no podemos satisfacer sus requerimientos específicos.\n\nSi desea obtener más información o explorar alternativas, no dude en contactarnos directamente.\n\nGracias por su interés en VV Health.\n\nAtentamente,\nEl equipo de VV Health`,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #2a5885;">Actualización sobre su cotización</h2>
            <p>Estimado/a ${userName},</p>
            <p>Lamentamos informarle que no podemos proceder con su cotización <strong>#${quotationId}</strong> en este momento. Esto puede deberse a que los productos solicitados no están disponibles o no podemos satisfacer sus requerimientos específicos.</p>
            <p>Si desea obtener más información o explorar alternativas, no dude en contactarnos directamente.</p>
            <p>Gracias por su interés en VV Health.</p>
            <p>Atentamente,<br>El equipo de VV Health</p>
          </div>
        `
      });
    }

    // Para otros estados, usamos el método anterior
    return await sendStatusUpdateNotification(quotation, userEmail, oldStatus, newStatus);
  } catch (error) {
    console.error('Error al enviar notificación de cambio de estado:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return null;
  }
};

/**
 * Envía un email de bienvenida cuando un usuario crea una cuenta
 * @param {Object} user - Objeto de usuario de Parse
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const notifyNewUser = async (user) => {
  try {
    return await sendWelcomeEmail(user);
  } catch (error) {
    console.error('Error al enviar email de bienvenida:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return null;
  }
};

/**
 * Actualiza el estado de una cotización y envía una notificación
 * @param {Object} quotation - Objeto de cotización de Parse
 * @param {string} newStatus - Nuevo estado de la cotización
 * @param {string} [userId] - ID del usuario que realiza el cambio (opcional)
 * @returns {Promise} - Promesa que se resuelve cuando la cotización es actualizada y la notificación enviada
 */
export const updateQuotationStatusWithNotification = async (quotation, newStatus, userId = null) => {
  try {
    // Guardar el estado actual antes de actualizarlo
    const oldStatus = quotation.get('status') || 'pending';
    
    // Si el estado no ha cambiado, no hacemos nada
    if (oldStatus === newStatus) {
      console.log(`El estado de la cotización ${quotation.id} ya es '${newStatus}'. No se realizará ningún cambio.`);
      return quotation;
    }
    
    console.log(`Actualizando estado de cotización ${quotation.id} de '${oldStatus}' a '${newStatus}'`);
    
    // Actualizar el estado
    quotation.set('status', newStatus);
    
    // Guardar el historial de cambios de estado
    const statusHistory = quotation.get('statusHistory') || [];
    statusHistory.push({
      status: newStatus,
      date: new Date().toISOString(),
      by: userId || 'system' // Usar el ID del usuario si está disponible, o 'system' por defecto
    });
    quotation.set('statusHistory', statusHistory);
    
    // Guardar la cotización
    await quotation.save(null, { useMasterKey: true });
    console.log(`Cotización ${quotation.id} actualizada con éxito al estado '${newStatus}'`);
    
    // Enviar notificación
    const notificationResult = await notifyStatusChange(quotation, oldStatus, newStatus);
    if (notificationResult) {
      console.log(`Notificación de cambio de estado enviada para cotización ${quotation.id}`);
    } else {
      console.warn(`No se pudo enviar la notificación de cambio de estado para cotización ${quotation.id}`);
    }
    
    return quotation;
  } catch (error) {
    console.error(`Error al actualizar estado de cotización ${quotation.id} a '${newStatus}':`, error);
    throw new Error(`Error al actualizar estado: ${error.message}`);
  }
};

/**
 * Envía un email al equipo de ventas cuando un usuario solicita información adicional
 * @param {Object} quotation - Objeto de cotización de Parse
 * @param {Object} user - Objeto de usuario de Parse
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const contactSalesTeam = async (quotation, user) => {
  try {
    if (!quotation) {
      console.warn('No se puede enviar consulta a ventas: cotización no disponible');
      return null;
    }

    // Obtener datos para la plantilla
    const userEmail = user ? user.get('email') : quotation.get('userEmail');
    const userName = user ? user.get('name') || user.get('firstName') : quotation.get('userName') || 'Cliente';
    const quotationId = quotation.id;
    const productType = quotation.get('productType') || 'Producto';
    const status = quotation.get('status') || 'pending';
    
    // Usar la plantilla de bienvenida adaptada para consulta de ventas
    const result = await sendTemplateEmail({
      to: 'ventas@vvhealthpr.com', // Email de ventas para recibir notificaciones
      templateId: TEMPLATE_IDS.WELCOME_QUOTATION,
      dynamicTemplateData: {
        nombre_cliente: userName,
        email_cliente: userEmail,
        tipo_producto: productType,
        id_cotizacion: quotationId,
        asunto: `Consulta sobre cotización #${quotationId}`,
        mensaje_adicional: `El cliente ha solicitado información adicional sobre su cotización #${quotationId} que actualmente está en estado: ${status}.`,
        app_url: getAppUrl()
      }
    });

    if (result) {
      console.log(`Email de consulta a ventas enviado exitosamente para la cotización ${quotationId}`);
    } else {
      console.warn(`No se pudo enviar el email de consulta a ventas para la cotización ${quotationId}`);
    }
    
    return result;
  } catch (error) {
    console.error('Error al enviar consulta a ventas:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return null;
  }
};

export default {
  notifyNewQuotation,
  notifyStatusChange,
  notifyNewUser,
  updateQuotationStatusWithNotification,
  contactSalesTeam
};
